import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Typography from "./Typography";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { LogoutOutlined, SettingFilled } from "@ant-design/icons";
import { Tooltip } from "antd";
import CreateProject from "components/dashboard/home/CreateProjectButton";
import Button from "./Button";
import { RootState } from "redux/store";
import { useSelector } from "react-redux";
import { useAccessControl } from "hooks/useAccessControl";

interface MenuItem {
  title: string;
  icon: React.ReactNode | JSX.Element;
  path?: string;
  subItems?: { title: string; path: string }[];
}

interface SidebarProps {
  toggleLogoutPopup: () => void;
  menuItems: MenuItem[];
  handleClick?: () => void;
  collapseSidebar?: boolean;
  onUpgradeClick?: () => void;
  onEnterpriseClick?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  toggleLogoutPopup,
  menuItems,
  collapseSidebar,
  onUpgradeClick,
  onEnterpriseClick,
}) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [openDropdowns, setOpenDropdowns] = useState<{
    [key: string]: boolean;
  }>({});

  const { userProfile } = useSelector(
    (state: RootState) => state.applicationData
  );

  const { isAdmin } = useAccessControl();

  const toggleDropdown = (title: string) => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  return (
    <div
      className={`${collapseSidebar ? "w-[80px] px-1 py-4" : "w-full p-[32px_10px]"} flex flex-col gap-4 bg-[#1A1C4E] text-white h-[100vh]`}
    >
      <div
        onClick={() => navigate(-1)}
        className={`${collapseSidebar && "text-center"} `}
      >
        <Typography
          variant="LG"
          text="Nozma"
          className="text-[#46CDEA] cursor-pointer"
          strong
        />
      </div>

      <Tooltip trigger="hover" title="Create" placement="right">
        <CreateProject
          className="py-2 bg-inherit border-[1px] border-[#fff] rounded-lg"
          showText={!collapseSidebar}
        />
      </Tooltip>

      <nav className="flex flex-col flex-1 min-h-0">
        <div className="flex-1 overflow-y-auto overflow-x-hidden space-y-1">
          {menuItems.map((item) => (
            <div key={item.title}>
              {item.subItems ? (
                <>
                  <button
                    onClick={() => toggleDropdown(item.title)}
                    className="flex items-center justify-between w-full p-2 rounded hover:bg-[#E7ECF3] hover:text-blue-primary"
                  >
                    <div className="flex items-center gap-2">
                      {item.icon}
                      <Typography variant="LG" text={item.title} />
                    </div>
                    {openDropdowns[item.title] ? (
                      <FiChevronUp />
                    ) : (
                      <FiChevronDown />
                    )}
                  </button>
                  {openDropdowns[item.title] && (
                    <div className="ml-4 mt-2 space-y-2">
                      {item.subItems.map((subItem) => (
                        <Link
                          key={subItem.title}
                          to={subItem.path}
                          className="block py-2 px-2 rounded hover:bg-[#E7ECF3] hover:text-blue-primary"
                        >
                          {subItem.title}
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              ) : item.title === "Reports" ? (
                <button
                  onClick={onEnterpriseClick}
                  className={`${collapseSidebar ? "flex justify-center items-center p-2" : "flex items-center gap-2 p-2"} w-full rounded-lg hover:bg-[#E7ECF3] hover:text-blue-primary`}
                >
                  {!collapseSidebar ? (
                    <>
                      <div>{item.icon}</div>
                      <Typography variant="LG" text={item.title} />
                    </>
                  ) : (
                    <Tooltip
                      trigger="hover"
                      placement="right"
                      title={item.title}
                    >
                      <div>{item.icon}</div>
                    </Tooltip>
                  )}
                </button>
              ) : (
                <Link
                  to={item.path || "#"}
                  className={`${collapseSidebar ? "flex justify-center items-center p-2" : "flex items-center gap-2 p-2"}  rounded-lg ${
                    item.path && pathname.includes(item.path)
                      ? "bg-[#E7ECF3] text-blue-primary"
                      : "hover:bg-[#E7ECF3] hover:text-blue-primary"
                  }`}
                >
                  {!collapseSidebar ? (
                    <>
                      <div>{item.icon}</div>
                      <Typography variant="LG" text={item.title} />
                    </>
                  ) : (
                    <Tooltip
                      trigger="hover"
                      placement="right"
                      title={item.title}
                    >
                      <div>{item.icon}</div>
                    </Tooltip>
                  )}
                </Link>
              )}
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-2 mt-2">
          {/* Credits Card - Only show when sidebar is not collapsed */}
          {!collapseSidebar && (
            <div>
              <div
                className={`bg-gray-100 rounded-lg ${collapseSidebar ? "p-1" : "p-3"}`}
              >
                {isAdmin && (
                  <Typography
                    variant="Base"
                    text="Admin"
                    className="text-black"
                  />
                )}
                {userProfile?.userDetails[0]?.subscription &&
                !userProfile?.userDetails[0]?.isBuyCreditProfessional ? (
                  <div>
                    <Typography
                      variant="LG"
                      text={`Plan : ${userProfile?.userDetails[0]?.subscription}`}
                      className="text-black"
                    />
                  </div>
                ) : null}
                {/* Credits Display */}
                <div className="flex justify-between items-center my-3">
                  <Typography
                    variant="Base"
                    text="Credits Used"
                    className="text-blue-primary"
                  />
                  <Typography
                    variant="Base"
                    text={`${userProfile?.userDetails[0]?.projectCreatedCount || 0}/${userProfile?.userDetails[0]?.projectCreateAccess || 0}`}
                    className="text-blue-primary !font-medium"
                  />
                </div>

                {/* Progress Bar */}
                <div className="w-full bg-[#1A1C4E] rounded-full h-2 mb-3">
                  <div
                    className="bg-[#E6F159] h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${
                        userProfile?.userDetails[0]?.projectCreatedCount &&
                        userProfile?.userDetails[0]?.projectCreateAccess
                          ? (userProfile.userDetails[0].projectCreatedCount /
                              userProfile.userDetails[0].projectCreateAccess) *
                            100
                          : 0
                      }%`,
                    }}
                  />
                </div>
                {/* Upgrade Button */}
                <Button
                  type="Primary"
                  size="Medium"
                  buttonText="Upgrade"
                  className="w-full py-2 !bg-[#E6F159] !text-black"
                  onClick={onUpgradeClick}
                />
              </div>
            </div>
          )}

          {/* Settings & Logout */}
          <div
            className={`${collapseSidebar ? "flex flex-col items-center gap-3 " : "flex justify-between"}`}
          >
            <Link
              to="/settings"
              className={`py-2 px-6 rounded-lg hover:bg-[#E7ECF3] hover:text-blue-primary flex flex-row gap-2 
              ${
                pathname.includes("/settings") &&
                "bg-[#E7ECF3] text-blue-primary"
              }`}
            >
              {collapseSidebar ? (
                <Tooltip trigger="hover" placement="right" title={"Settings"}>
                  <SettingFilled />
                </Tooltip>
              ) : (
                <Typography variant="LG" text="Settings" />
              )}
            </Link>
            <LogoutOutlined
              className="px-4 py-2 cursor-pointer"
              onClick={toggleLogoutPopup}
            />
          </div>
        </div>
      </nav>
    </div>
  );
};

export default Sidebar;
